import { produce } from "immer";
import * as actions from "../actions/actionTypes";
import { keyBy, mapValues, orderBy } from "lodash-es";
import { filterTypes, images } from "../../utils/constants/constants";
import {
  convertDateFiltersToLocalDraft,
  migrateDateFilters,
} from "../../utils/siteConfiguration/dateFilters";
import { customFilterTypes } from "../../utils/constants/filterConstants";
import {
  AdditionalTheme,
  TenantConfigResponse,
} from "../../types/tenantConfig";
import { MenuFilterResponse } from "../../types/menuFilters";
import {
  DataSourceFieldResponse,
  DataSourceWithFieldsResponse,
} from "../../types/dataSources";
import { LabelValue } from "../../types/types";

function extractFromObject<T>(item: { value: T } | T): T | undefined {
  if (item && typeof item === "object") {
    return "value" in item ? item.value : undefined;
  }

  return item;
}

export const setCustomDefaultSelected = (filter) => {
  const customFilterType = customFilterTypes.find(
    (x) => x.type === filter.name
  );

  if (customFilterType) {
    const { fn, direction, defaultSelected } = customFilterType;
    const withSortValue = filter.values.map((value) => ({
      value: extractFromObject(value),
      sortValue: fn(extractFromObject(value)),
    }));

    const values = orderBy(withSortValue, "sortValue", direction);

    const defaultValues = values
      .filter((x) => defaultSelected.includes(x.value))
      .map(extractFromObject);

    const newFilter = {
      ...filter,
      values: values.map(extractFromObject),
      defaultValues,
    };

    return newFilter;
  }

  return {
    ...filter,
    values: filter.values.map(extractFromObject),
  };
};

const image: SiteConfigurationFile = {
  imageName: "",
  preview: null,
  file: null,
};

type SiteConfigurationFile = {
  imageName: string;
  preview: null | string;
  file: File | null;
};

export function getInitialMenuFiltersState() {
  return {
    checked: false,
    list: [],
    dataSources: [],
    dependencyParentUuidStagedForDeleteMap: {},
    dataSourceFieldsByUuid: undefined,
    dataSourcesByUuid: undefined,
    dataSourceFieldOptionsByDataSourceUuid: undefined,
  };
}

const initialState: SiteConfigurationState & { processing: boolean } = {
  siteName: "",
  darkThemeEnabled: false,
  background: image,
  logo: image,
  favicon: image,
  customPrimaryColor: {
    checked: false,
    color: null,
    isValidColor: true,
  },
  additionalTheme: {},
  dateFilters: convertDateFiltersToLocalDraft(migrateDateFilters(null)),
  tenant: {
    name: "",
    uuid: null,
    description: null,
    aiEnabled: false,
  },
  domain: null,
  menuFilters: getInitialMenuFiltersState(),
  booleanFilters: [""],
  showHeaderKPI: false,
  aiRowLimitEnabled: true,
  filtersUpdateHeaderKPIs: false,
  errors: null,
  loading: false,
  success: null,
  loaded: false,
  processing: false,
  recacheMenuFiltersLoading: false,
  recacheMenuFilterLoading: {},
  datasourceFieldsLoading: false,
  preCache: false,
};

const reducer = produce((draft: typeof initialState, action) => {
  /** Meant to be cast into a known type */
  const unknownAction: unknown = action;
  switch (action.type) {
    case actions.GET_SITE_CONFIGURATION_START: {
      draft.loading = true;
      draft.success = null;
      draft.errors = null;
      break;
    }

    case actions.GET_SITE_CONFIGURATION_SUCCESS: {
      const action = unknownAction as { results: TenantConfigResponse };
      draft.dateFilters = convertDateFiltersToLocalDraft(
        migrateDateFilters(
          action.results.dateFilters && JSON.parse(action.results.dateFilters)
        )
      );

      if (action.results.additionalTheme) {
        draft.additionalTheme = JSON.parse(action.results.additionalTheme);
      }

      draft.menuFilters.list =
        action.results.menuFilters.map(assembleMenuFilter);

      images.forEach((item) => {
        draft[item.key].inputName = item.key;
        draft[item.key].label = item.label;
        draft[item.key].preview = action.results[item.key];
        draft[item.key].imageName = action.results[item.key];
      });

      const settings =
        action.results.settings != null
          ? JSON.parse(action.results.settings)
          : {};

      draft.menuFilters.checked = action.results.menuFilters.length > 0;
      draft.siteName = action.results.siteName;
      draft.customPrimaryColor.checked = !!action.results.customPrimaryColor;
      draft.customPrimaryColor.color = action.results.customPrimaryColor;
      draft.domain = action.results.domain;
      draft.darkThemeEnabled = action.results.darkThemeEnabled;
      draft.tenant = action.results.tenant;
      draft.showHeaderKPI = action.results.showHeaderKPI;
      draft.aiRowLimitEnabled = action.results.aiRowLimitEnabled ?? true;
      draft.filtersUpdateHeaderKPIs = !!settings?.filtersUpdateHeaderKPIs;
      draft.loading = false;
      draft.loaded = true;
      draft.preCache = action.results.preCache ?? false;

      break;
    }

    case actions.GET_SITE_CONFIGURATION_FAIL: {
      draft.errors = action.error;
      draft.loading = false;
      break;
    }

    case actions.UPDATE_SITE_CONFIGURATION_START: {
      draft.processing = true;
      draft.errors = null;
      break;
    }

    case actions.UPDATE_SITE_CONFIGURATION_SUCCESS: {
      draft.processing = false;
      draft.errors = null;
      break;
    }

    case actions.UPDATE_SITE_CONFIGURATION_FAIL: {
      draft.errors = action.errors;
      draft.processing = false;
      break;
    }

    case actions.GET_DATA_SOURCES_SUCCESS: {
      const action = unknownAction as {
        results: { sources: DataSourceWithFieldsResponse[] };
      };
      draft.menuFilters.dataSources = action.results.sources.map((source) => ({
        value: source.uuid,
        label: source.displayName,
      }));
      const dataSourcesByUuid = keyBy(action.results.sources, "uuid");
      draft.menuFilters.dataSourcesByUuid = dataSourcesByUuid;
      draft.menuFilters.dataSourceFieldsByUuid = keyBy(
        action.results.sources.map((dataSource) => dataSource.fields).flat(),
        "uuid"
      );
      draft.menuFilters.dataSourceFieldOptionsByDataSourceUuid = mapValues(
        dataSourcesByUuid,
        (source) =>
          source.fields.map((field) => ({
            label: field.defaultLabel,
            value: field.uuid,
          }))
      );
      break;
    }

    case actions.RECACHE_MENU_FILTERS_START:
      draft.recacheMenuFiltersLoading = true;
      break;

    case actions.RECACHE_MENU_FILTERS_SUCCESS:
      draft.recacheMenuFiltersLoading = false;
      break;

    case actions.RECACHE_MENU_FILTERS_FAIL:
      draft.recacheMenuFiltersLoading = false;
      break;

    case actions.RECACHE_MENU_FILTER_START:
      draft.recacheMenuFilterLoading = {
        ...draft.recacheMenuFilterLoading,
        [action.uuid]: true,
      };
      break;

    case actions.RECACHE_MENU_FILTER_SUCCESS: {
      const menuFilter = draft.menuFilters.list.find(
        (v) => v.uuid === action.uuid
      );
      if (menuFilter) {
        Object.assign(menuFilter, assembleMenuFilter(action.results.data));
      }
      draft.recacheMenuFilterLoading[action.uuid] = false;
      break;
    }

    case actions.RECACHE_MENU_FILTER_FAIL:
      draft.recacheMenuFilterLoading[action.uuid] = false;
      break;
  }

  return draft;
}, initialState);

function assembleMenuFilter(filter: MenuFilterResponse) {
  return {
    uuid: filter.uuid,
    dataSourceUuid: filter.dataSourceUuid,
    dataSourceFieldUuid: filter.dataSourceFieldUuid,
    sortDataSourceFieldUuid: filter.sortDataSourceFieldUuid,
    keyDataSourceFieldUuid: filter.keyDataSourceFieldUuid,
    sortOrder: filter.sortOrder,
    displayName: filter.displayName || "",
    hide: filter.hide || [],
    show: filter.show || [],
    type: filter.type || filterTypes[0].value,
    totalCount: filter.totalCount,
    values:
      setCustomDefaultSelected(filter)
        .values?.filter((value) => value?.toString().trim())
        .map((item) => ({
          value: extractFromObject(item),
          label: extractFromObject(item),
        })) || [],
    defaultValues: setCustomDefaultSelected(filter).defaultValues || [],
    dependencyParents: filter.dependencyParents,
    usesCustomValues: filter.usesCustomValues,
    labeledValues: filter.labeledValues,
    name: filter.name,
    fromApi: true,
    position: filter.position,
    additionalDataSourceFieldUuids: filter.additionalDataSourceFields.map(
      (v) => v.uuid
    ),
  };
}

export type SiteConfigurationMenuFilterState = ReturnType<
  typeof assembleMenuFilter
>;

export type SiteConfigurationMenuFiltersState = {
  checked: boolean;
  list: SiteConfigurationMenuFilterState[];
  dataSources: Array<LabelValue<string>>;
  dependencyParentUuidStagedForDeleteMap: Record<string, unknown>;
  dataSourceFieldsByUuid?: Record<string, DataSourceFieldResponse>;
  dataSourcesByUuid?: Record<string, DataSourceWithFieldsResponse>;
  dataSourceFieldOptionsByDataSourceUuid?: Record<
    string,
    Array<LabelValue<string>>
  >;
};

export type SiteConfigurationState = {
  siteName: string;
  darkThemeEnabled: boolean;
  background: SiteConfigurationFile;
  logo: SiteConfigurationFile;
  favicon: SiteConfigurationFile;
  customPrimaryColor: {
    checked: boolean;
    color: string | null;
    isValidColor: boolean;
  };
  additionalTheme: AdditionalTheme;
  dateFilters: ReturnType<typeof convertDateFiltersToLocalDraft>;
  tenant: {
    name: string;
    uuid: string | null;
    description: string | null;
    aiEnabled: boolean;
  };
  domain: string | null;
  menuFilters: SiteConfigurationMenuFiltersState;
  booleanFilters: string[];
  showHeaderKPI: boolean;
  aiRowLimitEnabled: boolean;
  filtersUpdateHeaderKPIs: boolean;
  errors: unknown;
  loading: boolean;
  success: unknown;
  loaded: boolean;
  recacheMenuFiltersLoading: boolean;
  recacheMenuFilterLoading: Record<string, boolean>;
  datasourceFieldsLoading: boolean;
  preCache: boolean;
};

export default reducer;
